// src/pages/Announcements.js

import React, {
    useMemo,
    useState,
} from "react";

import axios from "axios";


/* =========================================================
   API
   ========================================================= */

/*
 * IMPORTANT:
 *
 * After sendAdminNotification has been deployed in Azure,
 * copy its full Function URL from:
 *
 * Azure Portal
 * → Function App
 * → sendAdminNotification
 * → Get Function URL
 *
 * Then paste it below.
 */

const SEND_ADMIN_NOTIFICATION_URL =
    "https://oppiesfunc.azurewebsites.net/api/sendAdminNotification?code=LH1Tq4IOOlod5a9eCXZLKNiri75psGOrl5DzTbMYH-H2AzFuMbuwfw==";


/* =========================================================
   OPTIONS
   ========================================================= */

const AUDIENCE_OPTIONS = [
    {
        value: "ALL",
        label: "All OPPIES Users",
        description:
            "OpShoppers, OpShops and Stores",
    },
    {
        value: "OpShopper",
        label: "OpShoppers",
        description:
            "Regular OPPIES users",
    },
    {
        value: "OpShop",
        label: "OpShops",
        description:
            "Registered OpShop accounts",
    },
    {
        value: "Store",
        label: "Stores",
        description:
            "Registered secondhand Store accounts",
    },
];


const CATEGORY_OPTIONS = [
    {
        value: "announcement",
        label: "Announcement",
    },
    {
        value: "update",
        label: "Update",
    },
    {
        value: "bugfix",
        label: "Bug Fix",
    },
    {
        value: "maintenance",
        label: "Maintenance",
    },
    {
        value: "outage",
        label: "Outage",
    },
];


/* =========================================================
   COLOURS
   ========================================================= */

const OPPIES_BEIGE =
    "#bfa97c";

const OPPIES_GREEN =
    "#1f5130";

const TEXT_DARK =
    "#3c3329";

const TEXT_MUTED =
    "#857969";


/* =========================================================
   STYLES
   ========================================================= */

const styles = {
    page: {
        width: "100%",
        maxWidth: 1050,
        margin: "0 auto",
        padding: "6px 4px 60px",
    },

    header: {
        display: "flex",
        justifyContent:
            "space-between",
        alignItems: "flex-start",
        gap: 20,
        marginBottom: 26,
    },

    headerText: {
        minWidth: 0,
    },

    title: {
        margin: 0,
        color: TEXT_DARK,
        fontSize: 28,
        fontWeight: 800,
        letterSpacing: -0.5,
    },

    subtitle: {
        margin:
            "8px 0 0",
        maxWidth: 680,
        color: TEXT_MUTED,
        fontSize: 14,
        lineHeight: 1.6,
    },

    statusPill: {
        flexShrink: 0,
        padding:
            "7px 12px",
        borderRadius: 999,
        background:
            "#f6f1e7",
        border:
            "1px solid rgba(191,169,124,0.35)",
        color: OPPIES_GREEN,
        fontSize: 12,
        fontWeight: 800,
    },

    warningBox: {
        marginBottom: 22,
        padding:
            "14px 16px",
        background:
            "#fffaf0",
        border:
            "1px solid #eadbbd",
        borderRadius: 12,
        color: "#685a42",
        fontSize: 13,
        lineHeight: 1.55,
    },

    card: {
        background: "#fff",
        border:
            "1px solid #e8e2d8",
        borderRadius: 16,
        padding: 24,
        boxShadow:
            "0 8px 26px rgba(0,0,0,0.055)",
    },

    sectionTitle: {
        margin:
            "0 0 18px",
        color: TEXT_DARK,
        fontSize: 17,
        fontWeight: 800,
    },

    grid: {
        display: "grid",
        gridTemplateColumns:
            "repeat(auto-fit, minmax(240px, 1fr))",
        gap: 18,
        marginBottom: 20,
    },

    field: {
        display: "flex",
        flexDirection: "column",
        gap: 7,
    },

    label: {
        color: TEXT_DARK,
        fontSize: 13,
        fontWeight: 800,
    },

    helper: {
        color: TEXT_MUTED,
        fontSize: 11,
        lineHeight: 1.45,
    },

    select: {
        width: "100%",
        boxSizing:
            "border-box",
        padding:
            "11px 12px",
        border:
            "1px solid #ddd5c8",
        borderRadius: 10,
        background: "#fff",
        color: TEXT_DARK,
        fontSize: 14,
        outline: "none",
    },

    input: {
        width: "100%",
        boxSizing:
            "border-box",
        padding:
            "11px 12px",
        border:
            "1px solid #ddd5c8",
        borderRadius: 10,
        background: "#fff",
        color: TEXT_DARK,
        fontSize: 14,
        outline: "none",
    },

    textarea: {
        width: "100%",
        minHeight: 145,
        boxSizing:
            "border-box",
        padding:
            "12px 13px",
        border:
            "1px solid #ddd5c8",
        borderRadius: 10,
        background: "#fff",
        color: TEXT_DARK,
        fontSize: 14,
        fontFamily: "inherit",
        lineHeight: 1.55,
        resize: "vertical",
        outline: "none",
    },

    counter: {
        display: "flex",
        justifyContent:
            "flex-end",
        marginTop: 5,
        color: TEXT_MUTED,
        fontSize: 11,
    },

    preview: {
        marginTop: 22,
        padding: 18,
        borderRadius: 14,
        background:
            "linear-gradient(180deg, #faf7f0 0%, #f5f1e8 100%)",
        border:
            "1px solid #e7dfd1",
    },

    previewLabel: {
        marginBottom: 10,
        color: TEXT_MUTED,
        fontSize: 11,
        fontWeight: 800,
        textTransform:
            "uppercase",
        letterSpacing: 0.7,
    },

    pushCard: {
        maxWidth: 440,
        padding:
            "14px 15px",
        borderRadius: 13,
        background:
            "#ffffff",
        boxShadow:
            "0 4px 16px rgba(0,0,0,0.08)",
        border:
            "1px solid rgba(0,0,0,0.05)",
    },

    pushApp: {
        color: OPPIES_GREEN,
        fontSize: 11,
        fontWeight: 800,
        marginBottom: 5,
    },

    pushTitle: {
        color: "#222",
        fontSize: 14,
        fontWeight: 800,
        marginBottom: 4,
    },

    pushMessage: {
        margin: 0,
        color: "#555",
        fontSize: 13,
        lineHeight: 1.45,
        whiteSpace:
            "pre-wrap",
    },

    actions: {
        display: "flex",
        justifyContent:
            "flex-end",
        alignItems: "center",
        gap: 12,
        marginTop: 24,
        flexWrap: "wrap",
    },

    resetButton: {
        border:
            "1px solid #d8d0c3",
        background: "#fff",
        color: TEXT_MUTED,
        padding:
            "10px 16px",
        borderRadius: 10,
        fontSize: 13,
        fontWeight: 700,
        cursor: "pointer",
    },

    sendButton: {
        border: "none",
        background:
            OPPIES_GREEN,
        color: "#fff",
        padding:
            "11px 19px",
        borderRadius: 10,
        fontSize: 13,
        fontWeight: 800,
        cursor: "pointer",
        boxShadow:
            "0 5px 14px rgba(31,81,48,0.18)",
    },

    disabledButton: {
        opacity: 0.55,
        cursor: "not-allowed",
    },

    error: {
        marginTop: 18,
        padding:
            "13px 15px",
        borderRadius: 10,
        background:
            "#fff1ef",
        border:
            "1px solid #efccc6",
        color: "#94483f",
        fontSize: 13,
        lineHeight: 1.5,
    },

    success: {
        marginTop: 20,
        padding:
            "17px 18px",
        borderRadius: 12,
        background:
            "#f2f8f3",
        border:
            "1px solid #cde1d1",
    },

    successTitle: {
        margin:
            "0 0 9px",
        color: OPPIES_GREEN,
        fontSize: 15,
        fontWeight: 800,
    },

    resultGrid: {
        display: "grid",
        gridTemplateColumns:
            "repeat(auto-fit, minmax(120px, 1fr))",
        gap: 10,
        marginTop: 12,
    },

    resultItem: {
        padding:
            "10px 12px",
        background:
            "#ffffff",
        borderRadius: 9,
        border:
            "1px solid #e3ece5",
    },

    resultValue: {
        color: TEXT_DARK,
        fontSize: 18,
        fontWeight: 800,
    },

    resultLabel: {
        marginTop: 2,
        color: TEXT_MUTED,
        fontSize: 10,
        textTransform:
            "uppercase",
        letterSpacing: 0.5,
    },
};


/* =========================================================
   PAGE
   ========================================================= */

export default function Announcements() {
    const [
        audience,
        setAudience,
    ] = useState("OpShopper");

    const [
        category,
        setCategory,
    ] = useState(
        "announcement"
    );

    const [
        title,
        setTitle,
    ] = useState("");

    const [
        message,
        setMessage,
    ] = useState("");

    const [
        sending,
        setSending,
    ] = useState(false);

    const [
        error,
        setError,
    ] = useState("");

    const [
        result,
        setResult,
    ] = useState(null);


    /* =====================================================
       SELECTED AUDIENCE
    ===================================================== */

    const selectedAudience =
        useMemo(
            () =>
                AUDIENCE_OPTIONS.find(
                    (item) =>
                        item.value ===
                        audience
                ),
            [
                audience,
            ]
        );


    /* =====================================================
       RESET
    ===================================================== */

    const resetForm = () => {
        setAudience(
            "OpShopper"
        );

        setCategory(
            "announcement"
        );

        setTitle("");

        setMessage("");

        setError("");

        setResult(null);
    };


    /* =====================================================
       SEND
    ===================================================== */

    const handleSend =
        async () => {
            setError("");
            setResult(null);

            const cleanTitle =
                title.trim();

            const cleanMessage =
                message.trim();


            if (!cleanTitle) {
                setError(
                    "Please enter a notification title."
                );

                return;
            }


            if (!cleanMessage) {
                setError(
                    "Please enter a notification message."
                );

                return;
            }


            if (
                SEND_ADMIN_NOTIFICATION_URL.startsWith(
                    "PASTE_"
                )
            ) {
                setError(
                    "The sendAdminNotification Function URL has not been configured in Announcements.js yet."
                );

                return;
            }


            const audienceLabel =
                selectedAudience
                    ?.label ||
                audience;


            /*
             * Important safety step.
             *
             * Admin must explicitly confirm the
             * broadcast before anything is sent.
             */

            const confirmed =
                window.confirm(
                    `Send this ${category} notification to ${audienceLabel}?\n\n` +
                    `"${cleanTitle}"\n\n` +
                    `This will send a push notification to all registered devices in the selected audience.`
                );


            if (!confirmed) {
                return;
            }


            setSending(true);


            try {
                const response =
                    await axios.post(
                        SEND_ADMIN_NOTIFICATION_URL,
                        {
                            title:
                                cleanTitle,

                            message:
                                cleanMessage,

                            category,

                            audience,

                            createdBy:
                                "OPPIES Admin",
                        },
                        {
                            timeout:
                                120000,
                        }
                    );


                setResult(
                    response.data
                );


            } catch (
            requestError
            ) {
                console.error(
                    "[Announcements] Failed to send:",
                    requestError
                );


                const responseMessage =
                    requestError
                        ?.response
                        ?.data
                        ?.error;


                const details =
                    requestError
                        ?.response
                        ?.data
                        ?.details;


                setError(
                    details
                        ? `${responseMessage || "Unable to send notification."} ${details}`
                        : responseMessage ||
                        requestError
                            ?.message ||
                        "Unable to send notification."
                );


            } finally {
                setSending(false);
            }
        };


    const canSend =
        title.trim().length >
        0 &&
        message.trim().length >
        0 &&
        !sending;


    /* =====================================================
       RENDER
    ===================================================== */

    return (
        <div
            style={
                styles.page
            }
        >
            {/* =========================================
                HEADER
            ========================================= */}

            <div
                style={
                    styles.header
                }
            >
                <div
                    style={
                        styles.headerText
                    }
                >
                    <h1
                        style={
                            styles.title
                        }
                    >
                        Announcements
                    </h1>

                    <p
                        style={
                            styles.subtitle
                        }
                    >
                        Send important OPPIES
                        updates, bug fixes,
                        maintenance notices and
                        outage information directly
                        to selected users.
                    </p>
                </div>

                <div
                    style={
                        styles.statusPill
                    }
                >
                    Push Notifications
                </div>
            </div>


            {/* =========================================
                TESTING NOTE
            ========================================= */}

            <div
                style={
                    styles.warningBox
                }
            >
                <strong>
                    Current stage:
                </strong>{" "}
                this page sends the Firebase
                push notification and records
                the master announcement in
                Cosmos DB. We will connect
                these announcements to the
                in-app OPPIES Notifications
                page next.
            </div>


            {/* =========================================
                COMPOSER
            ========================================= */}

            <div
                style={
                    styles.card
                }
            >
                <h2
                    style={
                        styles.sectionTitle
                    }
                >
                    Create Announcement
                </h2>


                {/* AUDIENCE + CATEGORY */}

                <div
                    style={
                        styles.grid
                    }
                >
                    <div
                        style={
                            styles.field
                        }
                    >
                        <label
                            style={
                                styles.label
                            }
                            htmlFor="announcement-audience"
                        >
                            Audience
                        </label>

                        <select
                            id="announcement-audience"
                            value={
                                audience
                            }
                            onChange={(
                                event
                            ) => {
                                setAudience(
                                    event
                                        .target
                                        .value
                                );

                                setResult(
                                    null
                                );
                            }}
                            style={
                                styles.select
                            }
                            disabled={
                                sending
                            }
                        >
                            {AUDIENCE_OPTIONS.map(
                                (
                                    option
                                ) => (
                                    <option
                                        key={
                                            option.value
                                        }
                                        value={
                                            option.value
                                        }
                                    >
                                        {
                                            option.label
                                        }
                                    </option>
                                )
                            )}
                        </select>

                        <span
                            style={
                                styles.helper
                            }
                        >
                            {
                                selectedAudience
                                    ?.description
                            }
                        </span>
                    </div>


                    <div
                        style={
                            styles.field
                        }
                    >
                        <label
                            style={
                                styles.label
                            }
                            htmlFor="announcement-category"
                        >
                            Type
                        </label>

                        <select
                            id="announcement-category"
                            value={
                                category
                            }
                            onChange={(
                                event
                            ) => {
                                setCategory(
                                    event
                                        .target
                                        .value
                                );

                                setResult(
                                    null
                                );
                            }}
                            style={
                                styles.select
                            }
                            disabled={
                                sending
                            }
                        >
                            {CATEGORY_OPTIONS.map(
                                (
                                    option
                                ) => (
                                    <option
                                        key={
                                            option.value
                                        }
                                        value={
                                            option.value
                                        }
                                    >
                                        {
                                            option.label
                                        }
                                    </option>
                                )
                            )}
                        </select>

                        <span
                            style={
                                styles.helper
                            }
                        >
                            Used by the app to
                            identify the type of
                            announcement.
                        </span>
                    </div>
                </div>


                {/* TITLE */}

                <div
                    style={{
                        ...styles.field,
                        marginBottom:
                            18,
                    }}
                >
                    <label
                        style={
                            styles.label
                        }
                        htmlFor="announcement-title"
                    >
                        Title
                    </label>

                    <input
                        id="announcement-title"
                        type="text"
                        value={
                            title
                        }
                        onChange={(
                            event
                        ) => {
                            setTitle(
                                event
                                    .target
                                    .value
                            );

                            setResult(
                                null
                            );
                        }}
                        placeholder="e.g. OPPIES Update"
                        maxLength={
                            100
                        }
                        style={
                            styles.input
                        }
                        disabled={
                            sending
                        }
                    />

                    <div
                        style={
                            styles.counter
                        }
                    >
                        {
                            title.length
                        }
                        /100
                    </div>
                </div>


                {/* MESSAGE */}

                <div
                    style={
                        styles.field
                    }
                >
                    <label
                        style={
                            styles.label
                        }
                        htmlFor="announcement-message"
                    >
                        Message
                    </label>

                    <textarea
                        id="announcement-message"
                        value={
                            message
                        }
                        onChange={(
                            event
                        ) => {
                            setMessage(
                                event
                                    .target
                                    .value
                            );

                            setResult(
                                null
                            );
                        }}
                        placeholder="Enter the message you would like OPPIES users to receive..."
                        maxLength={
                            500
                        }
                        style={
                            styles.textarea
                        }
                        disabled={
                            sending
                        }
                    />

                    <div
                        style={
                            styles.counter
                        }
                    >
                        {
                            message.length
                        }
                        /500
                    </div>
                </div>


                {/* =====================================
                    PREVIEW
                ===================================== */}

                <div
                    style={
                        styles.preview
                    }
                >
                    <div
                        style={
                            styles.previewLabel
                        }
                    >
                        Notification Preview
                    </div>

                    <div
                        style={
                            styles.pushCard
                        }
                    >
                        <div
                            style={
                                styles.pushApp
                            }
                        >
                            OPPIES
                        </div>

                        <div
                            style={
                                styles.pushTitle
                            }
                        >
                            {
                                title.trim() ||
                                "OPPIES Update"
                            }
                        </div>

                        <p
                            style={
                                styles.pushMessage
                            }
                        >
                            {
                                message.trim() ||
                                "Your announcement will appear here."
                            }
                        </p>
                    </div>
                </div>


                {/* =====================================
                    ERROR
                ===================================== */}

                {error && (
                    <div
                        style={
                            styles.error
                        }
                    >
                        {error}
                    </div>
                )}


                {/* =====================================
                    SUCCESS
                ===================================== */}

                {result?.success && (
                    <div
                        style={
                            styles.success
                        }
                    >
                        <h3
                            style={
                                styles.successTitle
                            }
                        >
                            Announcement sent
                        </h3>

                        <div>
                            Audience:{" "}
                            <strong>
                                {
                                    result.audience
                                }
                            </strong>
                        </div>

                        <div
                            style={
                                styles.resultGrid
                            }
                        >
                            <div
                                style={
                                    styles.resultItem
                                }
                            >
                                <div
                                    style={
                                        styles.resultValue
                                    }
                                >
                                    {
                                        result.uniqueRecipients ??
                                        result.recipients ??
                                        0
                                    }
                                </div>

                                <div
                                    style={
                                        styles.resultLabel
                                    }
                                >
                                    Recipients
                                </div>
                            </div>


                            <div
                                style={
                                    styles.resultItem
                                }
                            >
                                <div
                                    style={
                                        styles.resultValue
                                    }
                                >
                                    {
                                        result.sent ??
                                        0
                                    }
                                </div>

                                <div
                                    style={
                                        styles.resultLabel
                                    }
                                >
                                    Sent
                                </div>
                            </div>


                            <div
                                style={
                                    styles.resultItem
                                }
                            >
                                <div
                                    style={
                                        styles.resultValue
                                    }
                                >
                                    {
                                        result.failed ??
                                        0
                                    }
                                </div>

                                <div
                                    style={
                                        styles.resultLabel
                                    }
                                >
                                    Failed
                                </div>
                            </div>
                        </div>
                    </div>
                )}


                {/* =====================================
                    ACTIONS
                ===================================== */}

                <div
                    style={
                        styles.actions
                    }
                >
                    <button
                        type="button"
                        onClick={
                            resetForm
                        }
                        disabled={
                            sending
                        }
                        style={{
                            ...styles.resetButton,

                            ...(sending
                                ? styles.disabledButton
                                : {}),
                        }}
                    >
                        Clear
                    </button>

                    <button
                        type="button"
                        onClick={
                            handleSend
                        }
                        disabled={
                            !canSend
                        }
                        style={{
                            ...styles.sendButton,

                            ...(!canSend
                                ? styles.disabledButton
                                : {}),
                        }}
                    >
                        {sending
                            ? "Sending..."
                            : "Send Announcement"}
                    </button>
                </div>
            </div>
        </div>
    );
}